import { useTour } from '@reactour/tour';
import { nanoid } from 'nanoid';
import { useEffect, useMemo } from 'react';

import { Modal } from '@/components/common/modal';
import { Button, ButtonVariant } from '@/components/form/button';
import { getTourStep } from '@/features/demo/steps';

import { IBatch, ITask } from '../../types';
import { PictureCard } from '../picture-card';

export interface IImageTask extends ITask {
  source?: string;

  result?: string;
}

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  batch: IBatch;

  task: IImageTask;

  onNext: () => void;

  onPrevious: () => void;

  index?: number;

  onEdit?: (task: ITask) => void;
}

export const TaskImageModal: React.FC<IProps> = ({
  task,
  batch,
  isOpen,
  toggle,
  onNext,
  index = 0,
  onPrevious,
  onEdit,
}) => {
  const { isOpen: isTourOpen, setCurrentStep, currentStep, steps } = useTour();

  useEffect(() => {
    if (isTourOpen && currentStep === getTourStep(steps, 'miioOSA'))
      setCurrentStep(getTourStep(steps, 'yZgJOSQ'));
  }, []);

  useEffect(() => {
    if (currentStep === getTourStep(steps, 'MlOWXEI') && isOpen) toggle();
  }, [currentStep]);

  const totalTaskCount = useMemo(() => batch!.tasks.length, [batch?.tasks]);

  const handleOnEdit = () => {
    if (!onEdit) return;
    onEdit(task);
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title=""
      subtitle={`Comparison ${index + 1}/${totalTaskCount}`}
      conatinerClassnames="w-full md:w-5/6"
    >
      <div className="flex justify-between">
        <div className="flex">
          <Button variant={ButtonVariant.Link} onClick={onPrevious}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          </Button>
        </div>

        <div className="flex gap-16 py-10">
          <div className="w-2/3 step-image-comparison">
            <PictureCard
              leftImage={`${task?.sourceImage}?no-cache=${nanoid()}`}
              rightImage={`${task?.resultImage}?no-cache=${nanoid()}`}
            />
          </div>
          <div className="flex flex-col w-1/3 gap-6">
            <DataItem label="Total" value={task?.resultStat.countTotal} />
            <DataItem label="Alive" value={task?.resultStat.countAlive} />
            <DataItem label="Dead" value={task?.resultStat.countDead} />
            <DataItem label="Budding" value={task?.resultStat.countBudding} />
            {onEdit ? (
              <div id="step-edit-cells-btn" className="flex flex-col gap-4">
                <Button onClick={handleOnEdit}>Edit Cells</Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex">
          <Button variant={ButtonVariant.Link} onClick={onNext}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const DataItem = ({
  label,
  value,
}: {
  label: 'Alive' | 'Dead' | 'Budding' | 'Total';
  value: string | number | undefined;
}) => {
  const _colors = {
    Alive: '#9EC597',
    Dead: '#E27ADC',
    Budding: '#E2CE7A',
    Total: '#FFF',
  };
  const colorClass = _colors[label];

  return (
    <div className="flex w-full">
      <div className="flex items-center flex-1 gap-3 font-bold text-black-dark">
        <div
          style={{ backgroundColor: colorClass }}
          className={'w-3 h-3 rounded-full'}
        ></div>
        <span>{label}</span>
      </div>
      <span className="flex-1 text-black-dark">
        {value === undefined ? 'N/A' : `${value} cells`}
      </span>
    </div>
  );
};
